<template>
  <div class="app-container">
    <!-- 第一部分：域名输入区域 -->
    <div class="input-section">
      <h2 class="section-title">微信域名检测</h2>
      <el-input
          type="textarea"
          :autosize="{ minRows: 8, maxRows: 8}"
          placeholder="请输入需要检测的域名，每行一条"
          v-model="domainList"
          class="domain-input"
      ></el-input>
      <div class="input-actions">
        <el-button type="primary" @click="startDetection" :loading="isDetecting">开始检测</el-button>
        <el-button type="info" @click="clearInput">清空输入</el-button>
      </div>
    </div>

    <!-- 第二部分：检测结果统计 -->
    <div class="stats-section" v-if="detectionResults.length > 0">
      <div class="stats">
        <div class="stat-item">
          <span class="stat-label">正常域名：</span>
          <span class="stat-value normal-count">{{ normalCount }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">封禁域名：</span>
          <span class="stat-value blocked-count">{{ blockedCount }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">检测失败：</span>
          <span class="stat-value failed-count">{{ failedCount }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">正在检测：</span>
          <span class="stat-value pending-count">{{ pendingCount }}</span>
        </div>
      </div>
      <div class="action-buttons">
        <el-button type="primary" @click="copyNormalDomains">复制正常域名</el-button>
        <el-button type="warning" @click="copyBlockedDomains">复制封禁域名</el-button>
      </div>
    </div>

    <!-- 第三部分：检测结果列表 -->
    <div class="results-section" v-if="detectionResults.length > 0">
      <h3 class="results-title">检测结果</h3>
      <div class="results-list">
        <div v-for="(item, index) in detectionResults" :key="index" class="result-item">
          <div class="result-content">
            <div class="result-domain">{{ item.domain }}</div>
            <div class="result-status">
              <el-tag effect="plain" :type="getStatusType(item.status)">
                <template v-if="item.status === '正在检测'">
                  <i class="el-icon-loading"></i>
                  <span>正在检测</span>
                </template>
                <span v-else>{{ item.status }}</span>
              </el-tag>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 空状态展示 -->
    <div class="empty-state" v-else>
      <div class="empty-content">
        <div class="empty-icon">
          <i class="el-icon-data-analysis"></i>
        </div>
        <h3 class="empty-title">准备开始检测</h3>
        <p class="empty-description">输入需要检测的域名，我们将为您分析微信域名状态</p>
        <div class="empty-features">
          <div class="feature-item">
            <i class="el-icon-check"></i>
            <span>实时检测域名状态</span>
          </div>
          <div class="feature-item">
            <i class="el-icon-s-promotion"></i>
            <span>多线路并行检测</span>
          </div>
          <div class="feature-item">
            <i class="el-icon-document-copy"></i>
            <span>一键复制结果</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

axios.timeout = 5000;
export default {
  name: 'App',
  data() {
    return {
      domainList: '',
      detectionResults: [],
      isDetecting: false
    }
  },
  computed: {
    normalCount() {
      return this.detectionResults.filter(item => item.status === '正常').length
    },
    blockedCount() {
      return this.detectionResults.filter(item => item.status === '封禁').length
    },
    failedCount() {
      return this.detectionResults.filter(item => item.status === '检测失败').length
    },
    pendingCount() {
      return this.detectionResults.filter(item => item.status === '正在检测' || item.status === '等待检测').length
    }
  },
  methods: {
    getStatusType(status) {
      switch (status) {
        case '正常':
          return 'success'
        case '封禁':
          return 'danger'
        case '正在检测':
          return 'warning'
        case '检测失败':
          return 'info'
        default:
          return ''
      }
    },
    startDetection() {
      const domains = this.domainList.split('\n').filter(domain => domain.trim())
      if (domains.length === 0) {
        this.$message.warning('请输入要检测的域名')
        return
      }

      this.isDetecting = true

      // 初始化结果列表
      this.detectionResults = domains.map(domain => ({
        domain: domain.trim(),
        status: '等待检测',
        time: ''
      }))

      // 并行检测（修复版）
      const detectionPromises = this.detectionResults.map((item, index) => {
        // 更新状态为"正在检测"
        this.$set(this.detectionResults, index, {
          ...item,
          status: '正在检测'
        })

        // ✅ return Promise，让 Promise.all 能正确等待
        return axios.get('/api/check.php?url=' + item.domain + '&key=20251014')
            .then(response => {
              let status;
              if (response.data === '正常') {
                status = '正常'
              } else if (response.data === '封禁') {
                status = '封禁'
              } else {
                status = '检测失败'
              }
              this.$set(this.detectionResults, index, {
                ...this.detectionResults[index],
                status: status,
                time: new Date().toLocaleString()
              })
            })
            .catch(error => {
              this.$set(this.detectionResults, index, {
                ...this.detectionResults[index],
                status: '检测失败',
                time: new Date().toLocaleString()
              })
            })
      })

      // 等待所有请求完成
      Promise.all(detectionPromises).then(() => {
        this.isDetecting = false
        this.$message.success(`检测完成，共检测 ${domains.length} 个域名`)
      })
    },
    clearInput() {
      this.domainList = ''
      this.detectionResults = []
      this.isDetecting = false
    },
    copyNormalDomains() {
      const normalDomains = this.detectionResults
          .filter(item => item.status === '正常')
          .map(item => item.domain)
          .join('\n')

      if (normalDomains) {
        this.copyToClipboard(normalDomains)
        this.$message.success('正常域名已复制到剪贴板')
      } else {
        this.$message.warning('没有正常域名可复制')
      }
    },
    copyBlockedDomains() {
      const blockedDomains = this.detectionResults
          .filter(item => item.status === '封禁')
          .map(item => item.domain)
          .join('\n')

      if (blockedDomains) {
        this.copyToClipboard(blockedDomains)
        this.$message.success('封禁域名已复制到剪贴板')
      } else {
        this.$message.warning('没有封禁域名可复制')
      }
    },
    copyToClipboard(text) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  width: 100%;
  height: 100%;
  overflow: hidden;
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', Arial, sans-serif;
}

#app {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.app-container {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 第一部分：输入区域 */
.input-section {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  animation: fadeIn 0.5s ease-in-out;
}

.section-title {
  color: #333;
  font-size: 24px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 600;
}

.domain-input {
  margin-bottom: 15px;
  border-radius: 8px;
}

/* 域名输入框滚动条样式 */
.domain-input ::-webkit-scrollbar {
  width: 4px;
}

.domain-input ::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.domain-input ::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.domain-input ::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.input-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
}

/* 第二部分：统计区域 */
.stats-section {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  animation: fadeIn 0.5s ease-in-out 0.2s both;
}

.stats {
  display: flex;
  gap: 40px;
}

.stat-item {
  display: flex;
  align-items: center;
  font-size: 18px;
}

.stat-label {
  color: #666;
  margin-right: 10px;
  font-weight: bold;
}

.stat-value {
  font-weight: 600;
  font-size: 20px;
}

.normal-count {
  color: #4CAF50;
}

.blocked-count {
  color: #f44336;
}

.action-buttons {
  display: flex;
  gap: 15px;
}

/* 第三部分：结果列表 */
.results-section {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 20px;
  flex: 1;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  animation: fadeIn 0.5s ease-in-out 0.4s both;
}

.results-title {
  color: #333;
  font-size: 18px;
  margin-bottom: 15px;
  font-weight: bold;
}

.results-list {
  height: calc(100% - 45px);
  overflow: auto;
}

/* 自定义滚动条 */
.results-list::-webkit-scrollbar {
  width: 4px;
}

.results-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.results-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.results-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 结果项样式 */
.result-item {
  border-bottom: 1px dashed #999999 !important;
  padding: 10px 0;
  transition: all 0.3s ease;
}

.result-item:hover {
  background-color: #f8f9fa;
}

.result-item:last-child {
  border-bottom: none;
}

.result-content {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 0 10px;
}

.result-domain {
  font-weight: 600;
  font-size: 16px;
  color: #333;
  flex: 1;
  min-width: 200px;
  margin-right: 20px;
}

.result-status {
  margin-right: 10px;
}

.result-time {
  font-size: 14px;
  color: #999;
  margin-left: auto;
}

/* 动画效果 */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .app-container {
    padding: 10px;
  }

  .section-title {
    font-size: 18px;
    margin-bottom: 15px;
  }

  .input-section {
    padding: 15px;
    margin-bottom: 15px;
  }

  .domain-input {
    margin-bottom: 10px;
  }

  .stats-section {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
    padding: 15px;
    margin-bottom: 15px;
  }

  .stats {
    width: 100%;
    gap: 15px;
    flex-wrap: wrap;
  }

  .stat-item {
    font-size: 14px;
    flex: 1;
    min-width: 45%;
  }

  .stat-value {
    font-size: 16px;
  }

  .action-buttons {
    width: 100%;
    gap: 10px;
  }

  .results-section {
    padding: 10px;
  }

  .results-title {
    font-size: 16px;
    margin-bottom: 10px;
  }

  .results-list {
    height: calc(100% - 35px);
  }

  /* 移动端结果项优化 */
  .result-item {
    padding: 10px 0;
    margin: 0 5px;
  }

  .result-content {
    padding: 0 5px;
  }

  .result-domain {
    font-size: 14px;
    min-width: 150px;
    margin-right: 15px;
  }

  .result-time {
    font-size: 12px;
  }

  /* 移动端空状态优化 */
  .empty-state {
    padding: 30px 15px;
  }

  .empty-content {
    padding: 0 10px;
  }

  .empty-icon {
    width: 70px;
    height: 70px;
    margin-bottom: 20px;
  }

  .empty-icon i {
    font-size: 32px;
  }

  .empty-title {
    font-size: 18px;
    margin-bottom: 10px;
  }

  .empty-description {
    font-size: 14px;
    margin-bottom: 25px;
  }

  .empty-features {
    gap: 10px;
  }

  .feature-item {
    padding: 12px;
    min-width: 90px;
  }

  .feature-item i {
    font-size: 18px;
    margin-bottom: 8px;
  }

  .feature-item span {
    font-size: 12px;
  }
}

/* 空状态样式 */
.empty-state {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  padding: 60px 20px;
  animation: fadeIn 0.5s ease-in-out;
}

.empty-content {
  text-align: center;
  max-width: 500px;
  padding: 0 20px;
}

.empty-icon {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 30px;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
  animation: pulse 2s infinite;
}

.empty-icon i {
  font-size: 48px;
  color: white;
}

.empty-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
}

.empty-description {
  font-size: 16px;
  color: #666;
  margin-bottom: 40px;
  line-height: 1.5;
}

.empty-features {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 20px;
}

.feature-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 10px;
  flex: 1;
  min-width: 120px;
  transition: all 0.3s ease;
}

.feature-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  background: #f0f1f5;
}

.feature-item i {
  font-size: 24px;
  color: #667eea;
  margin-bottom: 10px;
}

.feature-item span {
  font-size: 14px;
  color: #555;
  text-align: center;
}

/* 动画效果 */
@keyframes pulse {
  0% {
    box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
  }
  50% {
    box-shadow: 0 4px 30px rgba(102, 126, 234, 0.6);
  }
  100% {
    box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
  }
}

/* 确保页面无滚动条 */
.app-container {
  overflow: hidden;
}
</style>
